import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { ConsentProvider, ConsentBanner, useConsent } from "./components/consent/ConsentManager";
import { StagingAccessGate } from "./components/StagingAccessGate";

// Pages
import HomePage from "./pages/HomePage";
import LeistungenPage from "./pages/LeistungenPage";
import NewsPage from "./pages/NewsPage";
import NewsDetailPage from "./pages/NewsDetailPage";
import UeberUnsPage from "./pages/UeberUnsPage";
import KontaktPage from "./pages/KontaktPage";
import ImpressumPage from "./pages/ImpressumPage";
import DatenschutzPage from "./pages/DatenschutzPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      axios
        .get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setUser(res.data);
        })
        .catch(() => {
          localStorage.removeItem("token");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.access_token);
    const userRes = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${res.data.access_token}` },
    });
    setUser(userRes.data);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isAdminRoute = location.pathname.startsWith("/admin");
  if (isAdminRoute) return null;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0A2A43] text-white py-2 px-4 md:px-8 lg:px-16 text-sm">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-4 md:gap-6 flex-wrap">
            <a href="tel:054068983239" className="flex items-center gap-2 hover:text-[#D32F2F] transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="hidden sm:inline">05406 - 8983239</span>
            </a>
          </div>
          {/* Email in der Mitte */}
          <a href="mailto:info@gashi-betonbau.de" className="flex items-center gap-2 hover:text-[#D32F2F] transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>info@gashi-betonbau.de</span>
          </a>
          <div className="hidden md:flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Mo–Do: 08:00–17:00 | Fr: 08:00–13:00</span>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`sticky top-0 z-50 bg-white shadow-md transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_0e0aad54-d599-4098-9e14-5a2bc54d3dab/artifacts/zqzexpks_logo.png" 
                alt="Gashi Bau GmbH" 
                className="h-12 md:h-16"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <NavLink to="/" className={({ isActive }) => `font-semibold text-[#0A2A43] hover:text-[#D32F2F] transition-colors ${isActive ? 'text-[#D32F2F]' : ''}`} data-testid="nav-home">
                Startseite
              </NavLink>
              <NavLink to="/leistungen" className={({ isActive }) => `font-semibold text-[#0A2A43] hover:text-[#D32F2F] transition-colors ${isActive ? 'text-[#D32F2F]' : ''}`} data-testid="nav-leistungen">
                Leistungen
              </NavLink>
              <NavLink to="/news" className={({ isActive }) => `font-semibold text-[#0A2A43] hover:text-[#D32F2F] transition-colors ${isActive ? 'text-[#D32F2F]' : ''}`} data-testid="nav-news">
                News
              </NavLink>
              <NavLink to="/ueber-uns" className={({ isActive }) => `font-semibold text-[#0A2A43] hover:text-[#D32F2F] transition-colors ${isActive ? 'text-[#D32F2F]' : ''}`} data-testid="nav-ueber-uns">
                Über uns
              </NavLink>
              <NavLink to="/kontakt" className={({ isActive }) => `font-semibold text-[#0A2A43] hover:text-[#D32F2F] transition-colors ${isActive ? 'text-[#D32F2F]' : ''}`} data-testid="nav-kontakt">
                Kontakt
              </NavLink>
              <Link to="/kontakt" className="btn-primary text-sm" data-testid="nav-cta">
                Jetzt anfragen
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-[#0A2A43]"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col gap-4">
                <NavLink to="/" className="font-semibold text-[#0A2A43] hover:text-[#D32F2F]" onClick={() => setIsMenuOpen(false)}>
                  Startseite
                </NavLink>
                <NavLink to="/leistungen" className="font-semibold text-[#0A2A43] hover:text-[#D32F2F]" onClick={() => setIsMenuOpen(false)}>
                  Leistungen
                </NavLink>
                <NavLink to="/news" className="font-semibold text-[#0A2A43] hover:text-[#D32F2F]" onClick={() => setIsMenuOpen(false)}>
                  News
                </NavLink>
                <NavLink to="/ueber-uns" className="font-semibold text-[#0A2A43] hover:text-[#D32F2F]" onClick={() => setIsMenuOpen(false)}>
                  Über uns
                </NavLink>
                <NavLink to="/kontakt" className="font-semibold text-[#0A2A43] hover:text-[#D32F2F]" onClick={() => setIsMenuOpen(false)}>
                  Kontakt
                </NavLink>
                <Link to="/kontakt" className="btn-primary text-sm text-center" onClick={() => setIsMenuOpen(false)}>
                  Jetzt anfragen
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

// Footer Component
const Footer = () => {
  const location = useLocation();
  const [settings, setSettings] = useState({});
  const { openConsentSettings, revokeConsent } = useConsent();
  
  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data || {})).catch(() => {});
  }, []);

  const isAdminRoute = location.pathname.startsWith("/admin");
  if (isAdminRoute) return null;

  const socialLinks = [
    { url: settings.facebook_url, icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ), name: "Facebook" },
    { url: settings.instagram_url, icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    ), name: "Instagram" },
    { url: settings.linkedin_url, icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ), name: "LinkedIn" },
    { url: settings.youtube_url, icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ), name: "YouTube" },
  ].filter(link => link.url);

  return (
    <footer className="bg-[#0A2A43] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_0e0aad54-d599-4098-9e14-5a2bc54d3dab/artifacts/zqzexpks_logo.png" 
              alt="Gashi Bau GmbH" 
              className="h-16 mb-4"
            />
            <p className="text-white/80 text-sm leading-relaxed">
              Ihr zuverlässiger Partner für professionellen Betonbau und Rohbauarbeiten in der Region Osnabrück.
            </p>
            {/* Social Media Icons */}
            {socialLinks.length > 0 && (
              <div className="flex gap-3 mt-4">
                {socialLinks.map((link) => (
                  <a 
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white/80 hover:bg-[#D32F2F] hover:text-white transition-all"
                    title={link.name}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-barlow text-lg font-bold mb-4 uppercase tracking-wider">Kontakt</h4>
            <div className="space-y-3 text-sm text-white/80">
              <p>Gashi Bau GmbH</p>
              <p>Up de Heede 33</p>
              <p>49191 Belm</p>
              <p className="pt-2">
                <a href="tel:054068983239" className="hover:text-[#D32F2F] transition-colors">
                  Tel: 05406 - 8983239
                </a>
              </p>
              <p>
                <a href="mailto:info@gashi-betonbau.de" className="hover:text-[#D32F2F] transition-colors">
                  info@gashi-betonbau.de
                </a>
              </p>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="font-barlow text-lg font-bold mb-4 uppercase tracking-wider">Öffnungszeiten</h4>
            <div className="space-y-2 text-sm text-white/80">
              <p>Mo – Do: 08:00 – 17:00</p>
              <p>Fr: 08:00 – 13:00</p>
              <p className="pt-2 text-white/60">Sa – So: Geschlossen</p>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-barlow text-lg font-bold mb-4 uppercase tracking-wider">Links</h4>
            <div className="space-y-2 text-sm">
              <Link to="/leistungen" className="block text-white/80 hover:text-[#D32F2F] transition-colors">Leistungen</Link>
              <Link to="/news" className="block text-white/80 hover:text-[#D32F2F] transition-colors">News</Link>
              <Link to="/ueber-uns" className="block text-white/80 hover:text-[#D32F2F] transition-colors">Über uns</Link>
              <Link to="/kontakt" className="block text-white/80 hover:text-[#D32F2F] transition-colors">Kontakt</Link>
              <Link to="/impressum" className="block text-white/80 hover:text-[#D32F2F] transition-colors">Impressum</Link>
              <Link to="/datenschutz" className="block text-white/80 hover:text-[#D32F2F] transition-colors">Datenschutz</Link>
            </div>
          </div>
        </div>

        <div className="mt-10 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="footer-privacy-shorttext">
          <p>Datenschutz: <Link to="/datenschutz" className="hover:text-white underline underline-offset-4">Datenschutzerklärung</Link> | Kontakt: <a href="mailto:info@gashi-betonbau.de" className="hover:text-white underline underline-offset-4">info@gashi-betonbau.de</a></p>
          <div className="flex flex-wrap gap-2">
            <button onClick={openConsentSettings} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition" data-testid="footer-consent-settings-button">Consent-Einstellungen</button>
            <button onClick={revokeConsent} className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition" data-testid="footer-consent-revoke-button">Einwilligung widerrufen</button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/60">
          <p>© 2026 Gashi Bau GmbH</p>
          <div className="flex gap-4">
            <Link to="/impressum" className="hover:text-white transition-colors">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-white transition-colors">Datenschutz</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

function App() {
  useEffect(() => {
    // Seed initial data
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ConsentProvider>
          <StagingAccessGate>
            <div className="App min-h-screen flex flex-col">
              <Header />
              <main className="flex-grow">
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/leistungen" element={<LeistungenPage />} />
                  <Route path="/news" element={<NewsPage />} />
                  <Route path="/news/:slug" element={<NewsDetailPage />} />
                  <Route path="/ueber-uns" element={<UeberUnsPage />} />
                  <Route path="/unternehmen.html" element={<UeberUnsPage />} />
                  <Route path="/kontakt" element={<KontaktPage />} />
                  <Route path="/impressum" element={<ImpressumPage />} />
                  <Route path="/datenschutz" element={<DatenschutzPage />} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/*" element={<AdminDashboard />} />
                </Routes>
              </main>
              <Footer />
              <ConsentBanner />
              <Toaster position="top-right" />
            </div>
          </StagingAccessGate>
        </ConsentProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
